import { useSearchParams } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Button } from "@hub/ui/components/ui/button";
import { Skeleton } from "@hub/ui/components/ui/skeleton";
import { useSQDIPDashboard } from "../api/useSQDIPDashboard";
import { SummaryStrip } from "./SummaryStrip";
import { SafetySection } from "./SafetySection";
import { QualitySection } from "./QualitySection";
import { DeliverySection } from "./DeliverySection";
import { InventorySection } from "./InventorySection";
import { ProductivitySection } from "./ProductivitySection";
import { DateRangeSelector } from "./DateRangeSelector";
import { TeamSelector } from "./TeamSelector";

const AVAILABLE_TEAMS = ["default"];

export function TeamDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTeamParam = searchParams.get("team") ?? AVAILABLE_TEAMS[0];
  const teamParam = AVAILABLE_TEAMS.includes(rawTeamParam) ? rawTeamParam : AVAILABLE_TEAMS[0];
  const dateRange = searchParams.get("range") ?? "7d";

  const { summary, daily, queueStats, workers, loading, refetch } =
    useSQDIPDashboard(teamParam, dateRange);

  const handleTeamChange = (team: string) => {
    setSearchParams({ team, range: dateRange });
  };

  const handleDateRangeChange = (range: string) => {
    setSearchParams({ team: teamParam, range });
  };

  const scrollToDimension = (dimension: string) => {
    document.getElementById(dimension)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-64" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Team Dashboard</h1>
          <span className="text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/10 px-2 py-0.5 rounded">
            {teamParam}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <TeamSelector teams={AVAILABLE_TEAMS} value={teamParam} onChange={handleTeamChange} />
          <DateRangeSelector value={dateRange} onChange={handleDateRangeChange} />
          <Button variant="outline" size="sm" className="h-8" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {summary ? (
        <>
          <SummaryStrip summary={summary} onSelect={scrollToDimension} />
          <div className="space-y-4">
            <SafetySection daily={daily} />
            <QualitySection daily={daily} />
            <DeliverySection daily={daily} />
            <InventorySection daily={daily} queueStats={queueStats} />
            <ProductivitySection workers={workers} />
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-white/5 rounded-lg">
          <p className="text-sm text-gray-400">No data available — projection definitions need to be configured.</p>
        </div>
      )}
    </div>
  );
}
